import React, { useRef, useState } from "react";
import {
    Button,
    Dropdown,
    Form,
    Grid,
    Input,
    Modal,
    Segment,
} from "semantic-ui-react";
import Page from "../Page";
import { toast } from "../../common/Toast";
import LocalStorage from "../../../lib/LocalStorage";
import config from "../../../common/repository/config";
import constants from "../../../common/constants";
interface Client {
    _id: string;
    name: string;
    type: string;
    case_type_id?: string;
}
interface ClientDataToAdd {
    ClientId: string;
    CaseType: string;
}
interface UploadPayload {
    caseTypeName: string;
    treatmentType: string;
    letterHead: boolean;
    clientType: string;
    clientsDataToAdd: ClientDataToAdd[];
    tpaCompanyIds?: string[];
}
interface ClientApiResponse {
    success: boolean;
    message?: string;
    data?: {
        items?: Client[];
    };
}
const BulkUpload = () => {
    const [loading, setLoading] = useState(false);
    const [caseTypeName, setCaseTypeName] = useState("");
    const [caseTypeId, setCaseTypeId] = useState("");
    const [clients, setClients] = useState<Client[]>([]);
    const [tpaCompany, setTpaCompany] = useState<Client[]>([]);
    const [tpaCompanyId, setTpaCompanyIds] = useState<string[]>([]);
    const [clientIds, setClientIds] = useState<string[]>([]);
    const [treatmentType, setTreatmentType] = useState("");
    const [letterHeadType, setLetterHeadType] = useState<boolean>();
    const [clientType, setClientType] = useState("");
    const [file, setFile] = useState<File | null>(null);
    const [htmlLoading, setHtmlLoading] = useState(false);
    const [htmlOutput, setHtmlOutput] = useState("");
    const [htmlInput, setHtmlInput] = useState("");
    const [showModal, setShowModal] = useState(false);
    const fileRef = useRef<HTMLInputElement>(null);
    const treatmentTypeOptions = [
        {
            key: constants.treatmentType.surgical,
            text: constants.treatmentType.surgical,
            value: constants.treatmentType.surgical,
        },
        {
            key: constants.treatmentType.conservative,
            text: constants.treatmentType.conservative,
            value: constants.treatmentType.conservative,
        },
    ];
    const clientTypeOptions = [
        {
            key: constants.ClientType.tpa,
            text: constants.ClientType.tpa,
            value: constants.ClientType.tpa,
        },
        {
            key: constants.ClientType.company,
            text: constants.ClientType.company,
            value: constants.ClientType.company,
        },
    ];
    const letterHeadTypeOptions = [
        { key: "Yes", text: "Yes", value: true },
        { key: "No", text: "No", value: false },
    ];
    const getToken = () => {
        const token = LocalStorage.get(LocalStorage.KEY_AUTH_TOKEN);
        if (!token) {
            toast.error("Authentication token is missing.");
            return null;
        }
        return token;
    };
    const extractHtmlFromResponse = (response: any): string => {
        let html = "";
        if (typeof response === "string") {
            html = response;
        } else if (typeof response?.data?.items === "string") {
            html = response.data.items;
        } else if (typeof response?.data?.html === "string") {
            html = response.data.html;
        } else if (typeof response?.data === "string") {
            html = response.data;
        } else if (typeof response?.html === "string") {
            html = response.html;
        } else if (typeof response?.result?.html === "string") {
            html = response.result.html;
        } else if (typeof response?.result === "string") {
            html = response.result;
        }
        if (!html) {
            return "";
        }
        html = html.trim();
        const doctypeIndex = html.search(/<!DOCTYPE\s+html/i);
        const htmlTagIndex = html.search(/<html(?:\s|>)/i);
        let startIndex = -1;
        if (doctypeIndex !== -1) {
            startIndex = doctypeIndex;
        } else if (htmlTagIndex !== -1) {
            startIndex = htmlTagIndex;
        }
        if (startIndex === -1) {
            return "";
        }
        html = html.substring(startIndex);
        const closingHtmlMatch = html.match(/<\/html\s*>/i);
        if (!closingHtmlMatch || closingHtmlMatch.index === undefined) {
            return "";
        }
        const endIndex =
            closingHtmlMatch.index + closingHtmlMatch[0].length;
        return html.substring(0, endIndex).trim();
    };
    const generateHtml = async (
        text?: string,
        currentHtml?: string
    ) => {
        const token = getToken();
        if (!token) {
            return;
        }
        if (!file && !text?.trim() && !currentHtml?.trim()) {
            toast.error("Please select a PDF file or enter an instruction.");
            return;
        }
        try {
            setHtmlLoading(true);
            const formData = new FormData();
            if (file && !currentHtml?.trim()) {
                formData.append("format", file);
            }
            if (currentHtml?.trim()) {
                formData.append("html", currentHtml);
            }
            if (text?.trim()) {
                const modificationPrompt = `
Current HTML:
${htmlOutput}
Instruction:
${text.trim()}
`;
                formData.append("instructions", modificationPrompt);
            }
            const response = await fetch(
                `${config.apiBaseURL}/promptAndFormat/generate/html`,
                {
                    method: "POST",
                    headers: {
                        "x-access-token": token,
                    },
                    body: formData,
                }
            );
            const result = await response.json();
            if (!response.ok || result?.success === false) {
                toast.error(
                    result?.message || "Unable to generate HTML."
                );
                return;
            }
            const generatedHtml = extractHtmlFromResponse(result);
            if (!generatedHtml) {
                toast.error(
                    "Valid HTML document was not returned from the API."
                );
                return;
            }
            setHtmlOutput(generatedHtml);
            setHtmlInput("");
            toast.success("HTML generated successfully.");
        } catch (error) {
            toast.error(
                error instanceof Error
                    ? error.message
                    : "Something went wrong while generating HTML."
            );
        } finally {
            setHtmlLoading(false);
        }
    };
    const handlePdfChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const selectedFile = event.target.files?.[0];
        if (!selectedFile) {
            setFile(null);
            return;
        }
        const isPdf =
            selectedFile.type === "application/pdf" ||
            selectedFile.name.toLowerCase().endsWith(".pdf");
        if (!isPdf) {
            toast.error("Only PDF files are allowed.");
            event.target.value = "";
            setFile(null);
            return;
        }
        setFile(selectedFile);
        setHtmlOutput("");
        setHtmlInput("");
    };
    const handleConvertToHtml = async () => {
        if (!file) {
            toast.error("Please select a PDF file.");
            return;
        }
        await generateHtml();
    };
    const handleTryAgain = async () => {
        if (!htmlInput.trim()) {
            toast.error("Please enter text before trying again.");
            return;
        }
        await generateHtml(htmlInput);
    };
    const searchClients = async (
        type: string,
        isPrimaryClient: boolean
    ) => {
        if (isPrimaryClient && !caseTypeName.trim()) {
            toast.error("Please enter Case Type Name.");
            return;
        }
        const token = getToken();
        if (!token) {
            return;
        }
        try {
            setLoading(true);
            const requestBody: {
                clientType: string;
                caseTypeName?: string;
            } = {
                clientType: type,
            };
// Initial search uses the case type.
// The follow-up company search fetches all companies.
            if (isPrimaryClient) {
                requestBody.caseTypeName = caseTypeName.trim();
            }
            const response = await fetch(
                `${config.apiBaseURL}/client/find/caseType`,
                {
                    method: "POST",
                    headers: {
                        "x-access-token": token,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(requestBody),
                }
            );
            const result: ClientApiResponse = await response.json();
            if (!response.ok || !result.success) {
                toast.error(
                    result.message || "Failed to fetch clients."
                );
                if (isPrimaryClient) {
                    setClients([]);
                    setClientIds([]);
                    setCaseTypeId("");
                } else {
                    setTpaCompany([]);
                    setTpaCompanyIds([]);
                }
                return;
            }
            const items: Client[] = result.data?.items ?? [];
            if (isPrimaryClient) {
                setClients(items);
                setClientIds([]);
                const matchedCaseTypeId = items.find(
                    (client) => client.case_type_id
                )?.case_type_id;
                setCaseTypeId(matchedCaseTypeId || "");
                if (type === constants.ClientType.tpa) {
                    setTpaCompany([]);
                    setTpaCompanyIds([]);
                }
            } else {
                setTpaCompany(items);
                setTpaCompanyIds([]);
            }
            if (items.length === 0) {
                toast.error("No clients found.");
                return;
            }
            toast.success(result.message || "Clients found.");
        } catch (error) {
            toast.error(
                error instanceof Error
                    ? error.message
                    : String(error)
            );
            if (isPrimaryClient) {
                setClients([]);
                setClientIds([]);
                setCaseTypeId("");
            } else {
                setTpaCompany([]);
                setTpaCompanyIds([]);
            }
        } finally {
            setLoading(false);
        }
    };
    const handleCheckbox = (id: string, type: string) => {
        if (type === constants.ClientType.tpa) {
            if (clientIds.includes(id)) {
                setClientIds([]);
                setTpaCompany([]);
                setTpaCompanyIds([]);
                return;
            }
            if (clientIds.length > 0) {
                toast.error("Only one TPA can be selected at a time.");
                return;
            }
            setClientIds([id]);
// Fetch all companies without caseTypeName.
            void searchClients(constants.ClientType.company, false);
            return;
        }
        setClientIds((previous) =>
            previous.includes(id)
                ? previous.filter((item) => item !== id)
                : [...previous, id]
        );
    };
    const handleTpaCompanyCheckBox = (id: string) => {
        setTpaCompanyIds((previous) =>
            previous.includes(id)
                ? previous.filter((item) => item !== id)
                : [...previous, id]
        );
    };
    const uploadFile = async () => {
        if (!caseTypeName.trim()) {
            toast.error("Please enter case type first.");
            return;
        }
        if (!treatmentType) {
            toast.error("Please select treatment type first.");
            return;
        }
        if (!clientType) {
            toast.error("Please select client type first.");
            return;
        }
        if (letterHeadType === undefined) {
            toast.error("Please select Letter Head.");
            return;
        }
        const token = getToken();
        if (!token) {
            return;
        }
        if (clientIds.length === 0) {
            toast.error(
                clientType === constants.ClientType.tpa
                    ? "No TPA is selected."
                    : "No insurance company is selected."
            );
            return;
        }
        if (
            clientType === constants.ClientType.tpa &&
            tpaCompanyId.length === 0
        ) {
            toast.error("No insurance company is selected.");
            return;
        }
        const selectedClients = clients.filter((client) =>
            clientIds.includes(client._id)
        );
        if (selectedClients.length !== clientIds.length) {
            toast.error("Please search and select your clients again.");
            return;
        }
        const clientsDataToAdd: ClientDataToAdd[] = selectedClients.map(
            (client) => ({
                ClientId: client._id,
                CaseType: client.case_type_id || "",
            })
        );
        if (clientsDataToAdd.some((client) => !client.CaseType)) {
            toast.error("Case type ID is missing for a selected client.");
            return;
        }
        const payload: UploadPayload = {
            caseTypeName: caseTypeName.trim(),
            treatmentType,
            letterHead: letterHeadType,
            clientType,
            clientsDataToAdd,
        };
        if (clientType === constants.ClientType.tpa) {
            const selectedCompanies = tpaCompany.filter((company) =>
                tpaCompanyId.includes(company._id)
            );
            if (selectedCompanies.length !== tpaCompanyId.length) {
                toast.error(
                    "Please search and select insurance companies again."
                );
                return;
            }
            payload.tpaCompanyIds = selectedCompanies.map(
                (company) => company._id
            );
        }
        const formData = new FormData();
        formData.append("text", htmlOutput);
        formData.append("type", "report_format");
        formData.append("clients", JSON.stringify(payload));
        try {
            setLoading(true);
            const response = await fetch(
                `${config.apiBaseURL}/promptAndFormat/addPrompt`,
                {
                    method: "POST",
                    headers: {
                        "x-access-token": token,
                    },
                    body: formData,
                }
            );
            const result = await response.json();
            if (!response.ok || result?.success === false) {
                toast.error(result?.message || "Upload failed.");
                return;
            }
            toast.success(
                result?.message || "Report format uploaded successfully."
            );
            setShowModal(false);
        } catch (error) {
            toast.error(
                error instanceof Error
                    ? error.message
                    : "Something went wrong."
            );
        } finally {
            setLoading(false);
        }
    };
    const clearForm = () => {
        setCaseTypeName("");
        setCaseTypeId("");
        setClients([]);
        setTpaCompany([]);
        setClientIds([]);
        setTpaCompanyIds([]);
        setFile(null);
        setTreatmentType("");
        setClientType("");
        setLetterHeadType(undefined);
        if (fileRef.current) {
            fileRef.current.value = "";
        }
    };
    const clearHtmlConverter = () => {
        setFile(null);
        setHtmlOutput("");
        setHtmlInput("");
        if (fileRef.current) {
            fileRef.current.value = "";
        }
    };
    return (
        <Page
            title="Report Format Upload"
            description="Convert PDF report format to HTML and upload the generated report format."
        >
            <Segment>
                <Form>
                    <Grid stackable columns={2}>
                        <Grid.Column width={10}>
                            <Form.Field>
                                <label>PDF File</label>
                                <Input
                                    fluid
                                    type="file"
                                    accept="application/pdf,.pdf"
                                    input={{ ref: fileRef }}
                                    onChange={handlePdfChange}
                                />
                            </Form.Field>
                        </Grid.Column>
                        <Grid.Column width={6} verticalAlign="bottom">
                            <Button
                                positive
                                icon="file code outline"
                                content="Convert to HTML"
                                loading={htmlLoading}
                                disabled={!file || htmlLoading}
                                onClick={handleConvertToHtml}
                            />
                            <Button
                                basic
                                icon="undo"
                                content="Clear"
                                disabled={htmlLoading}
                                onClick={clearHtmlConverter}
                            />
                        </Grid.Column>
                    </Grid>
                </Form>
            </Segment>
            {htmlOutput && (
                <>
                    <Segment>
                        <div
                            style={{
                                fontSize: "18px",
                                fontWeight: 600,
                                marginBottom: "1rem",
                            }}
                        >
                            Rendered HTML Output
                        </div>
                        <iframe
                            title="Generated HTML Preview"
                            srcDoc={htmlOutput}
                            style={{
                                width: "100%",
                                minHeight: "1100px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                backgroundColor: "#fff",
                                display: "block",
                            }}
                        />
                    </Segment>
                    <Segment>
                        <Form>
                            <Form.Field>
                                <label>Modify HTML / Prompt</label>
                                <textarea
                                    value={htmlInput}
                                    onChange={(event) =>
                                        setHtmlInput(event.target.value)
                                    }
                                    placeholder="Enter instructions or text and click Try Again..."
                                    rows={8}
                                    style={{
                                        width: "100%",
                                        resize: "vertical",
                                        padding: "12px",
                                        border: "1px solid #ddd",
                                        borderRadius: "4px",
                                        fontFamily: "inherit",
                                        fontSize: "14px",
                                    }}
                                />
                            </Form.Field>
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    gap: "10px",
                                    marginTop: "1rem",
                                }}
                            >
                                <Button
                                    primary
                                    icon="refresh"
                                    content="Try Again"
                                    loading={htmlLoading}
                                    disabled={
                                        htmlLoading || !htmlInput.trim()
                                    }
                                    onClick={handleTryAgain}
                                />
                                <Button
                                    positive
                                    icon="check"
                                    content="Submit"
                                    disabled={htmlLoading}
                                    onClick={() => setShowModal(true)}
                                />
                            </div>
                        </Form>
                    </Segment>
                </>
            )}
            <Modal
                open={showModal}
                onClose={() => {
                    if (!loading) {
                        setShowModal(false);
                    }
                }}
                size="large"
                closeIcon={!loading}
            >
                <Modal.Header>Report Format Upload</Modal.Header>
                <Modal.Content scrolling>
                    <Form>
                        <Grid stackable columns={4}>
                            <Grid.Column>
                                <Form.Field>
                                    <label>Case Type Name</label>
                                    <Input
                                        fluid
                                        type="text"
                                        placeholder="Enter Case Type Name"
                                        value={caseTypeName}
                                        onChange={(event) => {
                                            setCaseTypeName(event.target.value);
                                            setCaseTypeId("");
                                            setClients([]);
                                            setTpaCompany([]);
                                            setClientIds([]);
                                            setTpaCompanyIds([]);
                                        }}
                                    />
                                </Form.Field>
                            </Grid.Column>
                            <Grid.Column>
                                <Form.Field>
                                    <label>Client Type</label>
                                    <Dropdown
                                        fluid
                                        selection
                                        placeholder="Select Client Type"
                                        options={clientTypeOptions}
                                        value={clientType}
                                        onChange={(_, { value }) => {
                                            setClientType(value as string);
                                            setClients([]);
                                            setTpaCompany([]);
                                            setClientIds([]);
                                            setTpaCompanyIds([]);
                                            setCaseTypeId("");
                                        }}
                                    />
                                </Form.Field>
                            </Grid.Column>
                            <Grid.Column>
                                <Form.Field>
                                    <label>Search</label>
                                    <Button
                                        primary
                                        icon="search"
                                        disabled={!clientType || loading}
                                        content="Search Clients"
                                        loading={loading}
                                        onClick={() =>
                                            void searchClients(clientType, true)
                                        }
                                    />
                                </Form.Field>
                            </Grid.Column>
                            <Grid.Column>
                                <Form.Field>
                                    <label>Treatment Type</label>
                                    <Dropdown
                                        fluid
                                        selection
                                        placeholder="Select Treatment Type"
                                        options={treatmentTypeOptions}
                                        value={treatmentType}
                                        onChange={(_, { value }) =>
                                            setTreatmentType(value as string)
                                        }
                                    />
                                </Form.Field>
                            </Grid.Column>
                            <Grid.Column>
                                <Form.Field>
                                    <label>Letter Head</label>
                                    <Dropdown
                                        fluid
                                        selection
                                        placeholder="Letter Head"
                                        options={letterHeadTypeOptions}
                                        value={letterHeadType}
                                        onChange={(_, { value }) =>
                                            setLetterHeadType(value as boolean)
                                        }
                                    />
                                </Form.Field>
                            </Grid.Column>
                        </Grid>
                        {clients.length > 0 && (
                            <>
                                <div
                                    style={{
                                        marginTop: "1.5rem",
                                        marginBottom: "0.75rem",
                                        fontWeight: 600,
                                        fontSize: "16px",
                                    }}
                                >
                                    Select {clientType}
                                </div>
                                <Grid columns={4} stackable>
                                    {clients.map((client) => (
                                        <Grid.Column key={client._id}>
                                            <Form.Checkbox
                                                label={client.name}
                                                checked={clientIds.includes(
                                                    client._id
                                                )}
                                                onChange={() =>
                                                    handleCheckbox(
                                                        client._id,
                                                        client.type
                                                    )
                                                }
                                            />
                                        </Grid.Column>
                                    ))}
                                </Grid>
                            </>
                        )}
                        {clientType === constants.ClientType.tpa &&
                            clientIds.length > 0 &&
                            tpaCompany.length > 0 && (
                                <>
                                    <div
                                        style={{
                                            marginTop: "1.5rem",
                                            marginBottom: "0.75rem",
                                            fontWeight: 600,
                                            fontSize: "16px",
                                        }}
                                    >
                                        Select Insurance Companies
                                    </div>
                                    <Grid columns={4} stackable>
                                        {tpaCompany.map((company) => (
                                            <Grid.Column key={company._id}>
                                                <Form.Checkbox
                                                    label={company.name}
                                                    checked={tpaCompanyId.includes(
                                                        company._id
                                                    )}
                                                    onChange={() =>
                                                        handleTpaCompanyCheckBox(
                                                            company._id
                                                        )
                                                    }
                                                />
                                            </Grid.Column>
                                        ))}
                                    </Grid>
                                </>
                            )}
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        basic
                        icon="close"
                        content="Cancel"
                        disabled={loading}
                        onClick={() => setShowModal(false)}
                    />
                    <Button
                        primary
                        icon="upload"
                        content="Upload"
                        loading={loading}
                        disabled={loading}
                        onClick={() => void uploadFile()}
                    />
                </Modal.Actions>
            </Modal>
        </Page>
    );
};
export default BulkUpload;
